import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { inviteMember, type OrgInvitationRow, revokeInvitation } from '../lib/org-members';
import { absolute } from '../lib/time';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// Pending invitations panel (task #19, `docs/plans/2026/08/27/101-user-lifecycle-vision`):
// a second READER of the roster's own resend call — see `apps/dashboard/CLAUDE.md`'s
// `<InviteForm/>` doctrine, "RESEND IS THE INVITE CALL". Presentational: the
// parent (`OrgMembers.tsx`) owns the query, `busy`, and the `run` closure that
// already gives `<InviteForm/>`/`<MemberRow/>` their busy/error/toast handling —
// this component adds no new machinery, it just calls into the same one.
//
// Renders NOTHING (no panel at all) when there are zero `pending` rows —
// mirroring `OrgMembers.tsx`'s CSV-button `Show` — rather than an empty panel
// nobody asked to see.

/** The rows worth showing here — every other status belongs to the roster's history, not this panel. */
function pendingOf(rows: readonly OrgInvitationRow[]): readonly OrgInvitationRow[] {
  return rows.filter((r) => r.status === 'pending');
}

/**
 * The role a resend would re-arm the row at, or `null` when there is none to
 * name — which is the whole point of the function.
 *
 * `OrgInvitationRow.role` is nullable because `InvitationView` is ONE projection
 * over both invitation kinds and a `platform` invitation grants no org role. This
 * panel only ever lists `org_member` rows, where the column is written on every
 * mint — but "only ever" is a claim about the API, not something this component
 * can check, and the wire type says the value may be absent.
 *
 * So a role-less row simply does not offer the resend. It used to default to
 * `member`, which is not a fallback but an INVENTED grant: `inviteMember` is
 * idempotent by rotation, so a resend at `member` would re-arm the stored row's
 * role to `member` — quietly demoting (or promoting) an invitation nobody meant
 * to touch, through the resend button. Revoke stays available, so such a
 * row is never stuck: it can be withdrawn and re-issued at a role someone chose.
 */
function resendRole(inv: OrgInvitationRow): OrgInvitationRow['role'] {
  return inv.role;
}

export function PendingInvitations(props: {
  readonly invitations: readonly OrgInvitationRow[];
  readonly busy: boolean;
  readonly token: string | null | undefined;
  readonly run: (fn: () => Promise<void>, successMsg?: string) => Promise<void>;
}): JSX.Element {
  const pending = (): readonly OrgInvitationRow[] => pendingOf(props.invitations);

  return (
    <Show when={pending().length > 0}>
      <Panel title={t('org_members.pending_invitations_title')} class="pending-invitations">
        <ul class="row-list">
          <For each={pending()}>
            {(inv) => (
              <li class="row-list__row pending-invitations__row" data-invitation={inv.id}>
                <div class="row-list__lead">
                  <div class="pending-invitations__id">
                    <div class="pending-invitations__email">{inv.email}</div>
                    <div class="pending-invitations__meta">
                      <Show when={inv.role}>
                        {(role) => (
                          <span class="pending-invitations__role">
                            {t(`org_members.role_${role()}`)}
                          </span>
                        )}
                      </Show>
                      <span class="pending-invitations__expires">
                        {t('org_members.col_expires')} {absolute(inv.expires_at_iso)}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="pending-invitations__actions">
                  <Show when={resendRole(inv)}>
                    {(role) => (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={props.busy}
                        onClick={() =>
                          void props.run(
                            () =>
                              inviteMember(
                                { by: 'email', email: inv.email },
                                role(),
                                props.token,
                              ).then(() => undefined),
                            t('org_members.toast_invited'),
                          )
                        }
                      >
                        {t('org_members.invite_resend')}
                      </Button>
                    )}
                  </Show>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={props.busy}
                    onClick={() =>
                      void props.run(
                        () => revokeInvitation(inv.id, props.token),
                        t('org_members.toast_revoked'),
                      )
                    }
                  >
                    {t('org_members.revoke')}
                  </Button>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Panel>
    </Show>
  );
}
